/**
 * Sidebar connection switcher (spec section 8): change the working database
 * without re-login. Same flow as the connections manager: set the context,
 * land on the dashboard, every consumer refetches on selectedConnection.
 */
import React from "react";
import {
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import StorageIcon from "@mui/icons-material/Storage";
import { useNavigate } from "react-router-dom";
import { useConnection } from "@/app/contexts/ConnectionContext";
import { connectionsApi, DatabaseConnection } from "@/utils/api";
import { MONO_FONT } from "@/shared/typography";
import { OperatorEmptyState } from "@/shared/operator";
import { useAppWorkspaceStore } from "@/app/workspace/appWorkspaceStore";
import { workspaceHasDirtyTabs } from "@/app/workspace/confirmCloseWorkspaceTab";
import { saveTabSession } from "@/app/workspace/tabSession";
import { confirmAsync } from "@/shared/dialogs";

export interface ConnectionSwitcherProps {
  collapsed: boolean;
}

export function ConnectionSwitcher({ collapsed }: ConnectionSwitcherProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { selectedConnection, setSelectedConnection } = useConnection();
  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
  const [connections, setConnections] = React.useState<DatabaseConnection[] | null>(null);
  const [loading, setLoading] = React.useState(false);

  // The menu is a QUICK switcher, not the manager: show the current
  // connection plus a handful of others; the full list lives on
  // /connections. Prevents a pathological menu when the platform
  // holds hundreds of connections.
  const MENU_CAP = 8;

  const open = (e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
    setLoading(true);
    connectionsApi
      .getAll(true)
      .then((list) => {
        const current = list.filter((c) => c.id === selectedConnection?.id);
        const others = list.filter((c) => c.id !== selectedConnection?.id);
        setConnections([...current, ...others].slice(0, MENU_CAP));
      })
      // Keep the stale-but-real list if a reopen fetch fails transiently;
      // only fall back to empty when there was nothing loaded yet.
      .catch(() => setConnections((prev) => prev ?? []))
      .finally(() => setLoading(false));
  };

  const pick = async (conn: DatabaseConnection) => {
    setAnchor(null);
    if (conn.id === selectedConnection?.id) return;
    if (workspaceHasDirtyTabs()) {
      const ok = await confirmAsync("Discard unsaved changes?");
      if (!ok) return;
    }
    // Persist the outgoing connection's tab set before wipe (dirty discarded).
    const prevId = selectedConnection?.id;
    if (prevId) {
      const s = useAppWorkspaceStore.getState();
      saveTabSession(prevId, {
        tabs: s.tabs.map(({ path, kind, title }) => ({ path, kind, title })),
        activePath: s.activePath,
      });
    }
    useAppWorkspaceStore.getState().reset();
    setSelectedConnection(conn);
    navigate("/dashboard");
  };

  const name = selectedConnection?.name ?? "No connection";

  if (!selectedConnection) {
    if (collapsed) {
      return (
        <Tooltip title="No connection selected" placement="right" arrow>
          <Box
            role="button"
            aria-label="No connection selected"
            tabIndex={0}
            onClick={() => navigate("/connections")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate("/connections");
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              my: 0.75,
              width: 40,
              py: 0.75,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              cursor: "pointer",
              "&:hover": { bgcolor: theme.palette.action.hover },
            }}
          >
            <StorageIcon sx={{ fontSize: 16, color: theme.palette.text.disabled }} />
          </Box>
        </Tooltip>
      );
    }

    return (
      <Box
        sx={{
          mx: 1.25,
          my: 0.75,
          px: 1,
          py: 0.5,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "8px",
        }}
      >
        <OperatorEmptyState
          kicker="Connection"
          tone="warn"
          action={
            <Typography
              component="button"
              type="button"
              onClick={() => navigate("/connections")}
              sx={{
                border: 0,
                background: "none",
                cursor: "pointer",
                fontFamily: MONO_FONT,
                fontSize: 10,
                letterSpacing: "0.12em",
                color: theme.palette.primary.main,
                p: 0,
              }}
            >
              MANAGE CONNECTIONS
            </Typography>
          }
        >
          NO CONNECTION SELECTED
        </OperatorEmptyState>
      </Box>
    );
  }

  const dot = (
    <Box
      sx={{
        width: 7,
        height: 7,
        borderRadius: "50%",
        bgcolor: theme.palette.success.main,
        boxShadow: `0 0 6px ${alpha(theme.palette.success.main, 0.8)}`,
        flexShrink: 0,
      }}
    />
  );

  return (
    <>
      <Tooltip title={collapsed ? name : ""} placement="right" arrow disableHoverListener={!collapsed}>
        <Box
          role="button"
          aria-label={name}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          tabIndex={0}
          onClick={open}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              // role="button" divs don't get the native Space handling —
              // without preventDefault, Space also scrolls the page.
              e.preventDefault();
              open(e as unknown as React.MouseEvent<HTMLElement>);
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mx: collapsed ? "auto" : 1.25,
            my: 0.75,
            px: collapsed ? 0 : 1.25,
            py: 0.75,
            width: collapsed ? 40 : "auto",
            justifyContent: collapsed ? "center" : "flex-start",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "8px",
            cursor: "pointer",
            "&:hover": { bgcolor: theme.palette.action.hover },
          }}
        >
          {dot}
          {!collapsed && (
            <>
              <Typography
                noWrap
                sx={{
                  flex: 1,
                  fontSize: 11.5,
                  fontFamily: MONO_FONT,
                  color: theme.palette.text.secondary,
                }}
              >
                {name}
              </Typography>
              <Typography sx={{ fontSize: 10, color: theme.palette.text.disabled }}>▾</Typography>
            </>
          )}
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
      >
        {loading && !connections && (
          <MenuItem disabled>
            <ListItemIcon>
              <CircularProgress size={14} />
            </ListItemIcon>
            Loading…
          </MenuItem>
        )}
        {(connections ?? []).map((conn) => (
          <MenuItem key={conn.id} onClick={() => pick(conn)} sx={{ fontSize: 13 }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              {conn.id === selectedConnection?.id ? (
                <CheckIcon sx={{ fontSize: 16 }} />
              ) : (
                <StorageIcon sx={{ fontSize: 16 }} />
              )}
            </ListItemIcon>
            {conn.name}
          </MenuItem>
        ))}
        {connections && connections.length === 0 && !loading && (
          <Box component="li" sx={{ listStyle: "none", px: 1.5, py: 1 }}>
            <OperatorEmptyState kicker="Connections">
              NO ACTIVE CONNECTIONS
            </OperatorEmptyState>
          </Box>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            navigate("/connections");
          }}
          sx={{ fontSize: 13 }}
        >
          Manage connections…
        </MenuItem>
      </Menu>
    </>
  );
}
